import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { MemberStatusEnum } from '../../../Enum/MemberStatusEnum';
import { DataGrid, GridColDef, gridClasses } from '@mui/x-data-grid';
import { isDefaultDate } from '../../../utils/common';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { parentUrl } from '../../../utils/constants';
import { setShowPreviousCharts } from '../../../store/memberDemographicsDetails.slice';
import { useEffect } from 'react';
import { fetchPopulationList } from '../../../store/adminMembers.slice';

const MemberPreviousChartsModal = () => {
    const { previousMemberCharts, showPreviousCharts } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useAppDispatch();
    const closePreviousChartsHandler = () => {
        dispatch(setShowPreviousCharts(false));
    };

    const modalSize = previousMemberCharts.length > 0 ? (isSmallScreen ? 'sm' : isMobile ? 'md' : 'lg') : 'sm';

    const columns: GridColDef[] = [
        {
            field: 'Mem_AHCCCS_ID',
            headerName: 'AHCCCS ID',
            minWidth: isSmallScreen ? 100 : 140,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: isSmallScreen ? 100 : 140,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: isSmallScreen ? 100 : 140,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: isSmallScreen ? 100 : 140,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : formatDate(row.DOB, 'MM/dd/yyyy')}`,
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: isSmallScreen ? 120 : 200,
            valueGetter: (value, row) => `${MemberStatusEnum[row.Status] || ''}`,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 160,
            valueGetter: (value, row) => `${population.find((el) => el.PopulationNumber === row.Population)?.PopulationName || ''}`,
        },
        {
            field: 'ContractingHealthPlan',
            headerName: 'Health Plan',
            minWidth: 140,
            flex: 1,
            valueGetter: (value, row) => `${row.ContractingHealthPlan?.Name || ''}`,
        },
    ];

    useEffect(() => {
        dispatch(fetchPopulationList());
    }, [dispatch]);

    return (
        <Dialog open={showPreviousCharts} onClose={closePreviousChartsHandler} fullWidth maxWidth={modalSize} fullScreen={isSmallScreen}>
            <DialogTitle>Previous Member Charts</DialogTitle>
            <DialogContent>
                {previousMemberCharts.length > 0 ? (
                    <div style={{ width: '100%', height: isSmallScreen ? '70vh' : '47vh' }}>
                        <DataGrid
                            rows={previousMemberCharts || []}
                            columns={columns}
                            onRowClick={(params) => {
                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                            }}
                            getRowHeight={() => 'auto'}
                            pageSizeOptions={[5]}
                            getRowId={(row) => row.Id}
                            sx={{
                                [`& .${gridClasses.cell}`]: {
                                    py: 1,
                                },
                            }}
                        />
                    </div>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" p={5}>
                        <Typography variant="body1">No previous charts found</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button sx={{ minWidth: '120px' }} variant="contained" onClick={closePreviousChartsHandler} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MemberPreviousChartsModal;
